import React, { useEffect, useState } from 'react';
import {
  TextField,
  Button,
  Select,
  MenuItem,
  Modal,
  InputLabel,
  FormControl,
  Box,
  Typography,
  Tooltip,
  Paper,
  Divider,
  IconButton,
} from '@mui/material';
import { Navigate, useNavigate } from 'react-router-dom';
import { Info as InfoIcon } from '@mui/icons-material';

const AddLession = () => {
  let navigate = useNavigate();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [subjectName, setSubjectName] = useState([]);
  const [className, setClassName] = useState([]);
  const [loadingBarProgress, setLoadingBarProgress] = useState('');
  const [topicName, setTopicName] = useState([]);
  const [formData, setFormData] = useState({
    class: '',
    subject: '',
    lession: '',
    Content: '',
    pdfFile: null,
  });

  // Info modal state
  const [showInfoModal, setShowInfoModal] = useState(false);

  const handleChange = (field, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [field]: value,
    }));
    if (field === 'class') {
      fetchSubjectddl(value);
    } else if (field === 'subject') {
      fetchTopicddl(formData.class, value);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setFormData((prevData) => ({
      ...prevData,
      pdfFile: file,
    }));
  };

  const handleSave = async () => {
    try {
      const url = process.env.REACT_APP_BASE_URL;
      const apiUrl = `${url}/OfflineLessoon`;
      const token = sessionStorage.getItem('token');
      setLoadingBarProgress(30);
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({
          "classId": formData.class,
          "subId": formData.subject || 2,
          "lessonName": formData.lession,
          "lessonContent": formData.Content,
          "lessionPdfUrl": "string"
        }),
      });

      if (response.ok) {
        setLoadingBarProgress(100);
        setShowSuccessModal(true);
        setFormData({
          class: '',
          subject: '',
          lession: '',
          Content: '',
        });
      } else {
        setLoadingBarProgress(0);
        console.error('Failed to save form data');
      }
    } catch (error) {
      console.error('Save operation error:', error);
    }
  };

  const handleReset = () => {
    setFormData({
      class: '',
      subject: '',
      lession: '',
      Content: '',
      pdfFile: null,
    });
  };

  const fetchClassddl = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${apiUrl}/Enquiry/ddlClassName`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({}),
      });

      if (response.ok) {
        const responseData = await response.json();

        if (responseData.data !== null) {
          setClassName(responseData);
        } else {
          console.error('No data found for classes');
        }

        if (responseData.msg && responseData.msg !== 'Record Not Found') {
          console.error('API error:', responseData.msg);
        }
      } else {
        console.error('Failed to fetch class data');
      }
    } catch (error) {
      console.error('API request error:', error);
    }
  };

  const fetchSubjectddl = async (classId) => {
    try {
      const apiUrl = `${process.env.REACT_APP_BASE_URL}/ClassMapping/GetSubjectMapping`;
      const token = sessionStorage.getItem('token');
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({
          classId: parseInt(classId, 10),
        }),
      });
      if (response.ok) {
        const responseData = await response.json();
        if (responseData && responseData.length > 0) {
          setSubjectName(responseData);
        } else {
          console.error('No data found for subjects');
        }
      } else {
        console.error('Failed to fetch subject data');
      }
    } catch (error) {
      console.error('API request error:', error);
    }
  };

  const fetchTopicddl = async (classId, subjectId) => {
    try {
      const url = process.env.REACT_APP_BASE_URL;
      const apiUrl = `${url}/Question/ddlLession`;
      const token = sessionStorage.getItem('token');
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({
          classId,
          subjectId,
        }),
      });

      if (response.ok) {
        const responseData = await response.json();

        if (responseData.data !== null) {
          setTopicName(responseData);
        } else {
          console.error('No data found for topics');
        }

        if (responseData.msg && responseData.msg !== 'Record Not Found') {
          console.error('API error:', responseData.msg);
        }
      } else {
        console.error('Failed to fetch topic data');
      }
    } catch (error) {
      console.error('API request error:', error);
    }
  };

  useEffect(() => {
    fetchClassddl();
  }, []);

  // Info sections data
  const infoSections = [
    {
      title: "📋 Page Overview",
      content:
        "The Add Lesson page allows authorized staff to create a new offline lesson record. Select a class and subject, enter the lesson name and content, then click SAVE to submit.",
    },
    {
      title: "📝 Form Fields",
      items: [
        "Class * – Select the class for which the lesson is being created. Required. Selecting a class automatically loads the available subjects.",
        "Subject * – Select the subject for the lesson. Loads after a class is selected. Required.",
        "Lesson – Enter the name or title of the lesson.",
        "Lesson Content – Enter the full content or description of the lesson (multi-line text area).",
      ],
    },
    {
      title: "⚙️ Actions",
      items: [
        "SAVE – Submits the new lesson to the system. Disabled until both Class and Subject are selected. Shows a success dialog on completion.",
        "RESET – Clears all form fields so you can start over.",
      ],
    },
    {
      title: "✅ After Saving",
      content:
        "A confirmation dialog appears confirming the lesson was added successfully. Click OK to close and return to the previous page.",
    },
  ];

  return (
    <Box p={3}>
      <Box
        sx={{
          backgroundColor: '#0B1F40', // dark navy blue
          padding: '12px 24px',
          boxShadow: 3,
          borderRadius: '4px 4px 0 0',
          margin: '2px auto',
          maxWidth: "100%",
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Typography variant="h4" sx={{ color: '#fff', fontWeight: 'bold' }}>
          Add Lesson
        </Typography>

        {/* Info Icon Button */}
        <Tooltip title="View page information & guide" arrow>
          <IconButton
            onClick={() => setShowInfoModal(true)}
            size="small"
            sx={{
              color: "rgba(255,255,255,0.75)",
              backgroundColor: "rgba(255,255,255,0.1)",
              border: "1.5px solid rgba(255,255,255,0.25)",
              borderRadius: "50%",
              width: 30,
              height: 30,
              ml: 0.5,
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "rgba(100,181,246,0.25)",
                borderColor: "#64B5F6",
                color: "#64B5F6",
                transform: "scale(1.1)",
              },
            }}
          >
            <InfoIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Paper elevation={3} sx={{ padding: 2, marginBottom: 2, marginTop: 1 }}>
        <FormControl fullWidth sx={{ marginBottom: 2 }}>
          <InputLabel>Class *</InputLabel>
          <Select
            value={formData.class}
            label="Class *"
            onChange={(e) => handleChange('class', e.target.value)}
          >
            {className.map((item) => (
              <MenuItem key={item.classId} value={item.classId}>
                {item.className}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth sx={{ marginBottom: 2 }}>
          <InputLabel>Subject *</InputLabel>
          <Select
            value={formData.subject}
            label="Subject *"
            onChange={(e) => handleChange('subject', e.target.value)}
          >
            {subjectName.map((item) => (
              <MenuItem key={item.subjectId} value={item.subjectId}>
                {item.subjectName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* <FormControl fullWidth sx={{ marginBottom: 2 }}>
          <InputLabel>Lession *</InputLabel>
          <Select
            value={formData.lesson}
            label="Lession *"
            onChange={(e) => handleChange('lesson', e.target.value)}
          >
            {topicName.map((item) => (
              <MenuItem key={item.lessionId} value={item.lessionName}>
                {item.lessionName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          fullWidth
          label="Video Link"
          value={formData.videoLink}
          onChange={(e) => handleChange('videoLink', e.target.value)}
          sx={{ marginBottom: 2 }}
        /> */}
        <TextField
          fullWidth
          label="Lesson "
          value={formData.lession}
          onChange={(e) => handleChange('lession', e.target.value)}
          sx={{ marginBottom: 2 }}
        />
        <TextField
          fullWidth
          label="Lesson Content"
          multiline
          rows={4}
          value={formData.Content}
          onChange={(e) => handleChange('Content', e.target.value)}
          sx={{ marginBottom: 2 }}
        />
        {/* <Box display="flex" alignItems="center" marginBottom={2}>
          <InputLabel htmlFor="pdfFile" sx={{ marginRight: 2 }}>
            Topic Path (Max 2 MB)
          </InputLabel>
          <input
            type="file"
            accept=".pdf"
            id="pdfFile"
            onChange={handleFileChange}
            sx={{ marginBottom: 2 }}
          />
        </Box> */}
      </Paper>

      <Box>
        <Tooltip title={(!formData.class || !formData.subject) && "Please fill required fields"} arrow>
          <span>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              style={{
                marginRight: "10px",
                width: "90px",
                height: "50px",
                padding: "10px 0",
                fontWeight: 'bold'
              }}
              disabled={!formData.class || !formData.subject}
            >
              SAVE
            </Button>
          </span>
        </Tooltip>
        <Button
          variant="contained"
          color="error"
          onClick={handleReset}
          style={{
            width: "90px",
            height: "50px",
            padding: "10px 0",
            fontWeight: 'bold'
          }}
        >
          RESET
        </Button>
      </Box>

      {/* ===== Info Modal ===== */}
      <Modal
        open={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        aria-labelledby="info-modal-title"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95%', sm: 520 },
            maxHeight: '85vh',
            bgcolor: 'background.paper',
            borderRadius: 3,
            boxShadow: '0 8px 40px rgba(11,31,61,0.22)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Modal Header */}
          <Box
            sx={{
              backgroundColor: '#0B1F3D',
              padding: '14px 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoIcon sx={{ color: '#64B5F6', fontSize: 22 }} />
              <Typography id="info-modal-title" variant="h6" sx={{ color: '#fff', fontWeight: 700 }}>
                Add Lesson — Page Guide
              </Typography>
            </Box>
            {/* Close Button */}
           
          </Box>

          {/* Modal Body — scrollable */}
          <Box sx={{ overflowY: 'auto', padding: '20px 24px', flex: 1 }}>
            {infoSections.map((section, idx) => (
              <Box key={idx} sx={{ mb: 2.5 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    color: '#0B1F3D',
                    mb: 0.8,
                    fontSize: '0.97rem',
                  }}
                >
                  {section.title}
                </Typography>

                {section.content && (
                  <Typography
                    variant="body2"
                    sx={{ color: '#444', lineHeight: 1.7, fontSize: '0.875rem' }}
                  >
                    {section.content}
                  </Typography>
                )}

                {section.items && (
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {section.items.map((item, i) => (
                      <Box
                        component="li"
                        key={i}
                        sx={{
                          fontSize: '0.875rem',
                          color: '#444',
                          lineHeight: 1.75,
                          mb: 0.3,
                        }}
                      >
                        {item}
                      </Box>
                    ))}
                  </Box>
                )}

                {idx < infoSections.length - 1 && (
                  <Divider sx={{ mt: 2, borderColor: 'rgba(11,31,61,0.08)' }} />
                )}
              </Box>
            ))}
          </Box>

          {/* Modal Footer */}
          <Box
            sx={{
              padding: '12px 24px',
              borderTop: '1px solid rgba(11,31,61,0.1)',
              display: 'flex',
              justifyContent: 'flex-end',
              backgroundColor: '#f9fafb',
            }}
          >
            <Button
              variant="contained"
              onClick={() => setShowInfoModal(false)}
              sx={{
                backgroundColor: '#0B1F3D',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': { backgroundColor: '#1a3560' },
              }}
            >
              Got it
            </Button>
          </Box>
        </Box>
      </Modal>

      <Modal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        aria-labelledby="success-modal-title"
        aria-describedby="success-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 16px' }}>
            <Typography id="success-modal-title" variant="h6" sx={{ color: '#fff' }}>
              Lesson Added ✅
            </Typography>
          </Box>

          {/* Content */}
          <Box sx={{ padding: '20px' }}>
            <Typography id="success-modal-description" sx={{ mb: 2 }}>
              The Lesson has been added successfully.
            </Typography>

            {/* Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate(-1); // go back after closing modal
                }}
                variant="contained"
                color="primary"
              >
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

    </Box>
  );
};

export default AddLession;